import { supabase } from './supabase'
import { captureException } from './errorTracking'
import { secureRPCWithSchema } from './sqlInjectionProtection'

/**
 * Compliance Reporting Service
 * Generates HIPAA and GDPR compliance reports
 */

/**
 * Generate HIPAA compliance report
 */
export const generateHIPAAReport = async (organizationId, periodStart, periodEnd) => {
  try {
    const reportId = await secureRPCWithSchema('generate_compliance_report', {
      org_id: organizationId,
      report_type_param: 'hipaa',
      period_start_param: periodStart,
      period_end_param: periodEnd,
    })

    // Fetch the generated report
    const { data: report, error: fetchError } = await supabase
      .from('compliance_reports')
      .select('*')
      .eq('id', reportId)
      .single()

    if (fetchError) throw fetchError

    return report
  } catch (error) {
    console.error('Error generating HIPAA report:', error)
    captureException(error, { context: 'generate_hipaa_report' })
    throw error
  }
}

/**
 * Generate GDPR compliance report
 */
export const generateGDPRReport = async (organizationId, periodStart, periodEnd) => {
  try {
    const reportId = await secureRPCWithSchema('generate_compliance_report', {
      org_id: organizationId,
      report_type_param: 'gdpr',
      period_start_param: periodStart,
      period_end_param: periodEnd,
    })

    // Fetch the generated report
    const { data: report, error: fetchError } = await supabase
      .from('compliance_reports')
      .select('*')
      .eq('id', reportId)
      .single()

    if (fetchError) throw fetchError

    return report
  } catch (error) {
    console.error('Error generating GDPR report:', error)
    captureException(error, { context: 'generate_gdpr_report' })
    throw error
  }
}

/**
 * Get compliance reports
 */
export const getComplianceReports = async (organizationId, reportType = null, limit = 20) => {
  try {
    let query = supabase
      .from('compliance_reports')
      .select('*')
      .eq('organization_id', organizationId)
      .order('generated_at', { ascending: false })
      .limit(limit)

    if (reportType) {
      query = query.eq('report_type', reportType)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching compliance reports:', error)
    captureException(error, { context: 'get_compliance_reports' })
    throw error
  }
}

/**
 * Get compliance score trend
 */
export const getComplianceScoreTrend = async (organizationId, reportType, months = 6) => {
  try {
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - months)

    const { data, error } = await supabase
      .from('compliance_reports')
      .select('compliance_score, generated_at, issues_found, issues_resolved')
      .eq('organization_id', organizationId)
      .eq('report_type', reportType)
      .gte('generated_at', startDate.toISOString())
      .order('generated_at', { ascending: true })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching compliance score trend:', error)
    captureException(error, { context: 'get_compliance_score_trend' })
    throw error
  }
}

